import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Container,
  Card,
  CardContent,
  CardActions,
  Button,
  Typography,
  Chip,
  Box,
  IconButton,
  Rating,
  Link,
  LinearProgress,
} from '@mui/material';
import {
  Favorite,
  FavoriteBorder,
  Delete,
  OpenInNew,
  Lock,
  LockOpen,
  Edit,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { sourcesApi } from '../services/api';
import { Source } from '../types';
import { useAuth } from '../context/AuthContext';
import SourcesFilterCard from '../components/SourcesFilterCard';
import SourcesListHeader from '../components/SourcesListHeader';

const SourcesList: React.FC = () => {
  const { preferences, user } = useAuth();
  const [sources, setSources] = useState<Source[]>([]);
  const [allSources, setAllSources] = useState<Source[]>([]); // For extracting filter options
  const [totalCount, setTotalCount] = useState(0); // Track total filtered records
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [pageSize, setPageSize] = useState(preferences?.preferredPageSize || 20);
  const [currentPage, setCurrentPage] = useState(1);

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [showFavorites, setShowFavorites] = useState(false);
  const [minRating, setMinRating] = useState('');

  // Advanced filter states
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState('');
  const [selectedAuthor, setSelectedAuthor] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Privacy filter states
  const [mySourcesFilter, setMySourcesFilter] = useState('all');
  const [showOthersPublic, setShowOthersPublic] = useState(true);

  // Debounced search term for API calls
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);

  // Update pageSize when user preferences change
  useEffect(() => {
    if (preferences?.preferredPageSize) {
      setPageSize(preferences.preferredPageSize);
      setCurrentPage(1); // Reset to first page when page size changes
    }
  }, [preferences]);

  // Debounce search term - wait 200ms after user stops typing before updating
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 200);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch all public sources on mount to populate filter options
  useEffect(() => {
    const fetchAllSources = async () => {
      try {
        const sources = await sourcesApi.getPublicSources();
        setAllSources(sources);
      } catch (error) {
        console.error('Failed to fetch all sources:', error);
      }
    };

    fetchAllSources();
  }, []);

  // Fetch filtered sources when filters change
  useEffect(() => {
    const fetchSources = async () => {
      setLoading(true);
      try {
        const params: any = {};
        if (debouncedSearchTerm) params.search = debouncedSearchTerm;
        if (selectedTags.length > 0) params.tags = selectedTags.join(',');
        if (showFavorites) params.favorites = true;
        if (minRating) params.minRating = parseInt(minRating);
        if (selectedPlatform) params.platform = selectedPlatform;
        if (selectedAuthor) params.author = selectedAuthor;
        if (dateFrom) params.dateFrom = dateFrom;
        if (dateTo) params.dateTo = dateTo;
        if (mySourcesFilter) params.mySourcesFilter = mySourcesFilter;
        if (!showOthersPublic) params.showOthersPublic = 'false';
        params.pageSize = pageSize; // Use user's preferred page size
        params.page = currentPage; // Use current page number

        const result = await sourcesApi.getSources(params);
        setSources(result.data);
        setTotalCount(result.totalCount);
      } catch (error) {
        console.error('Failed to fetch sources:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSources();
  }, [debouncedSearchTerm, selectedTags, showFavorites, minRating, selectedPlatform, selectedAuthor, dateFrom, dateTo, mySourcesFilter, showOthersPublic, pageSize, currentPage]);

  // Update URL params only when debounced search term changes (not on every keystroke)
  // TEMPORARILY DISABLED to test if this is the cause of focus loss
  // useEffect(() => {
  //   const newParams: any = {};
  //   if (debouncedSearchTerm) newParams.search = debouncedSearchTerm;
  //   if (selectedTags.length > 0) newParams.tags = selectedTags.join(',');
  //   if (showFavorites) newParams.favorites = 'true';
  //   if (minRating) newParams.minRating = minRating;
  //   if (selectedPlatform) newParams.platform = selectedPlatform;
  //   if (selectedAuthor) newParams.author = selectedAuthor;
  //   if (dateFrom) newParams.dateFrom = dateFrom;
  //   if (dateTo) newParams.dateTo = dateTo;
  //   if (mySourcesFilter !== 'all') newParams.mySourcesFilter = mySourcesFilter;
  //   if (!showOthersPublic) newParams.showOthersPublic = 'false';
  //   if (currentPage > 1) newParams.page = currentPage;
  //   setSearchParams(newParams);
  // }, [debouncedSearchTerm, selectedTags, showFavorites, minRating, selectedPlatform, selectedAuthor, dateFrom, dateTo, mySourcesFilter, showOthersPublic, currentPage, setSearchParams]);

  const handleToggleFavorite = async (source: Source) => {
    try {
      const updatedSource = await sourcesApi.toggleFavorite(source.id);
      setSources(sources.map(s => s.id === source.id ? updatedSource : s));
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
    }
  };

  const handleTogglePrivacy = async (source: Source) => {
    try {
      const updatedSource = await sourcesApi.togglePrivacy(source.id);
      setSources(sources.map(s => s.id === source.id ? updatedSource : s));
    } catch (error) {
      console.error('Failed to toggle privacy:', error);
    }
  };

  const handleDelete = async (source: Source) => {
    if (window.confirm(`Are you sure you want to delete "${source.name}"?`)) {
      try {
        await sourcesApi.deleteSource(source.id);
        setSources(sources.filter(s => s.id !== source.id));
      } catch (error) {
        console.error('Failed to delete source:', error);
      }
    }
  };

  const allTags = useMemo(() => {
    const tagSet = new Set<string>();
    allSources.forEach(source => {
      source.tags.forEach(tag => tagSet.add(tag.name));
    });
    return Array.from(tagSet).sort();
  }, [allSources]);

  const allPlatforms = useMemo(() => {
    const platformSet = new Set<string>();
    allSources.forEach(source => {
      if (source.sourcePlatform) platformSet.add(source.sourcePlatform);
    });
    return Array.from(platformSet).sort();
  }, [allSources]);

  const allAuthors = useMemo(() => {
    const authorSet = new Set<string>();
    allSources.forEach(source => {
      if (source.author?.name) authorSet.add(source.author.name);
    });
    return Array.from(authorSet).sort();
  }, [allSources]);

  const handleSearchChange = useCallback((newValue: string) => {
    setSearchTerm(newValue);
  }, []);

  const handlePageChange = useCallback((_event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
    // Scroll to top of page
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const clearAllFilters = useCallback(() => {
    setSearchTerm('');
    setSelectedTags([]);
    setShowFavorites(false);
    setMinRating('');
    setSelectedPlatform('');
    setSelectedAuthor('');
    setDateFrom('');
    setDateTo('');
    setMySourcesFilter('all');
    setShowOthersPublic(true);
    setCurrentPage(1); // Reset to first page
  }, []);

  const hasActiveFilters = useMemo(() => {
    return searchTerm || selectedTags.length > 0 || showFavorites || minRating ||
           selectedPlatform || selectedAuthor || dateFrom || dateTo ||
           mySourcesFilter !== 'all' || !showOthersPublic;
  }, [searchTerm, selectedTags, showFavorites, minRating, selectedPlatform, selectedAuthor, dateFrom, dateTo, mySourcesFilter, showOthersPublic]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleFavoritesToggle = useCallback(() => {
    setShowFavorites(!showFavorites);
  }, [showFavorites]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleAdvancedToggle = useCallback(() => {
    setShowAdvanced(!showAdvanced);
  }, [showAdvanced]);

  // Determine card background color based on ownership and privacy status
  const getCardBackgroundColor = (source: Source): string => {
    const isOwnSource = source.addedByUserId === user?.userId;

    if (isOwnSource && !source.isPublic) {
      return 'rgba(239, 83, 80, 0.06)'; // Own Private - soft red
    }
    if (isOwnSource && source.isPublic) {
      return 'rgba(76, 175, 80, 0.06)'; // Own Public - soft green
    }
    return 'rgba(255, 255, 255, 0)'; // Others' Public - default white
  };

  return (
    <Container maxWidth="lg">
      {/* Header section - memoized to prevent re-renders when loading state changes */}
      <SourcesListHeader
        recordsShown={sources.length}
        totalCount={totalCount}
        pageSize={pageSize}
        currentPage={currentPage}
        onPageChange={handlePageChange}
      />

      {/* Filter card - memoized to prevent re-renders when loading state changes */}
      <SourcesFilterCard
        searchTerm={searchTerm}
        onSearchChange={handleSearchChange}
        isLoading={loading}
        selectedTags={selectedTags}
        onTagsChange={setSelectedTags}
        minRating={minRating}
        onMinRatingChange={setMinRating}
        showFavorites={showFavorites}
        onFavoritesToggle={handleFavoritesToggle}
        showAdvanced={showAdvanced}
        onAdvancedToggle={handleAdvancedToggle}
        selectedPlatform={selectedPlatform}
        onPlatformChange={setSelectedPlatform}
        selectedAuthor={selectedAuthor}
        onAuthorChange={setSelectedAuthor}
        dateFrom={dateFrom}
        onDateFromChange={setDateFrom}
        dateTo={dateTo}
        onDateToChange={setDateTo}
        mySourcesFilter={mySourcesFilter}
        onMySourcesFilterChange={setMySourcesFilter}
        showOthersPublic={showOthersPublic}
        onShowOthersPublicChange={setShowOthersPublic}
        allTags={allTags}
        allPlatforms={allPlatforms}
        allAuthors={allAuthors}
        onClearFilters={clearAllFilters}
        hasActiveFilters={hasActiveFilters}
        onAddSource={() => navigate('/sources/add')}
      />

      {/* Loading indicator - progress bar shown during search */}
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Results container with smooth fade transition during loading */}
      <Box
        sx={{
          opacity: loading ? 0.6 : 1,
          transition: 'opacity 250ms ease-in-out',
          pointerEvents: loading ? 'none' : 'auto',
        }}
      >
        {sources.length === 0 ? (
          <Card>
            <CardContent>
              <Typography variant="h6" align="center" color="text.secondary">
                No sources found. {hasActiveFilters ? 'Try adjusting your filters.' : 'Add your first source!'}
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 3 }}>
            {sources.map((source) => (
            <Card
              key={source.id}
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: getCardBackgroundColor(source),
              }}
            >
              <CardContent sx={{ flexGrow: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Typography variant="h6" component="h2" noWrap>
                    {source.name}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <IconButton
                      size="small"
                      onClick={() => handleTogglePrivacy(source)}
                      title={source.isPublic ? 'Make Private' : 'Make Public'}
                    >
                      {source.isPublic ? <LockOpen color="success" /> : <Lock color="warning" />}
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleToggleFavorite(source)}
                    >
                      {source.isFavorite ? <Favorite color="error" /> : <FavoriteBorder />}
                    </IconButton>
                  </Box>
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {source.description}
                </Typography>

                {source.author && (
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Author:</strong> {source.author.name}
                  </Typography>
                )}

                {source.sourcePlatform && (
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Platform:</strong> {source.sourcePlatform}
                  </Typography>
                )}

                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Created by:</strong> {source.addedByUserName}
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Rating value={source.rating} size="small" readOnly />
                  <Typography variant="body2" sx={{ ml: 1 }}>
                    ({source.rating}/5)
                  </Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  {source.tags.map((tag) => (
                    <Chip
                      key={tag.id}
                      label={tag.name}
                      size="small"
                      sx={{
                        mr: 0.5,
                        mb: 0.5,
                        backgroundColor: tag.color || '#e0e0e0',
                        color: '#fff'
                      }}
                    />
                  ))}
                </Box>

                <Typography variant="caption" color="text.secondary">
                  Added: {new Date(source.dateAdded).toLocaleDateString()}
                </Typography>
              </CardContent>

              <CardActions>
                {source.url && (
                  <Button
                    size="small"
                    startIcon={<OpenInNew />}
                    component={Link}
                    href={source.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Open
                  </Button>
                )}

                <Button
                  size="small"
                  onClick={() => navigate(`/sources/${source.id}`)}
                >
                  Details
                </Button>

                {source.addedByUserId === user?.userId && (
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => navigate(`/sources/${source.id}/edit`)}
                    title="Edit source"
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                )}

                {source.addedByUserId === user?.userId && (
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDelete(source)}
                  >
                    <Delete />
                  </IconButton>
                )}
              </CardActions>
            </Card>
          ))}
        </Box>
      )}
      </Box>
    </Container>
  );
};

export default SourcesList;